// Listing-mode switcher for the Defect Reporting module.
//
// The five listing views are ROUTES, not local state — mirrors
// src/blank/BlankLayout.tsx (the reference) and src/companies/CompaniesLayout.tsx
// (the first adopter), which in turn mirror prod's InventoryLayout /
// ComponentLayout breadcrumb dropdown (aeronetsoftware/cloud/aeronet-frontend
// @develop). Modes as routes make deep-links, back/forward and a bookmarked
// report work for free; a useState mode gives none of that.
//
//   <base>/list    → DefectDataGrid viewMode="table"   ┐ mode routes, wrapped by
//   <base>/kanban  → DefectDataGrid viewMode="kanban"  │ this layout's <Outlet/>
//   <base>/mel     → MelReport                         │
//   <base>/oos     → OosReport                         │
//   <base>/ata     → AtaDistribution                   ┘
//   <base>/:id, /create → SIBLINGS of the modes (own Header each)
//
// This replaces a local Chip + Menu switcher that held `mode` in useState. No
// mode was added or removed — see index.tsx for the before/after route table.
//
// LOCAL ADAPTATION vs prod: prod keys its breadcrumb off ABSOLUTE paths
// ("/inventory/parts") because those modules mount at a fixed path. This module
// does not — App.tsx routes it at `menuItem.to + "/*"` straight from the backend
// nav, so the same module sits at a different path per tenant. Hardcoding a base
// would break every tenant mounted elsewhere, so the base is derived from the
// live pathname instead.
import { useEffect } from "react";

import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> dropdown label. Keys are the child route paths, and they are
 * the SAME five keys the pre-route switcher persisted, so a stored mode from
 * before this change still resolves.
 *
 * `list` is "List", not "DataGrid View", even though that breaks the local
 * "<X> View" symmetry with Kanban: `list` is SHARED chrome and 14 of the 17
 * modules that declare it label it "List" (Aaron, on the same deviation in
 * flight-expenses: "why does it not say list like other modules"). The other
 * four labels here stay bespoke because those views genuinely are. If the
 * local family is preferred over the cross-module convention, this one line
 * is the whole revert.
 */
export const breadcrumbMenu: { [segment: string]: string } = {
  list: "List",
  kanban: "Kanban View",
  mel: "MEL Report",
  oos: "OOS Report",
  ata: "ATA Distribution",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Every reported defect",
  kanban: "Defects by status",
  mel: "Deferrals with due dates",
  oos: "Aircraft out of service",
  ata: "Defects per ATA chapter",
};

export const DEFAULT_MODE_SEGMENT = "list";

// Remember the last mode so landing on the bare module root returns the user
// where they left off (PATTERNS.md mode-switch section, key shape
// `<module>.viewMode`). Routes stay the source of truth for WHICH mode is
// showing; localStorage only records the last one.
//
// ⚠️ THE KEY IS `defect_reporting`, THE DIRECTORY IS `defect-reporting`. This is
// the only module in the codebase where the two differ, and the mismatch is
// load-bearing: it is the key users already have persisted. Do NOT "normalise"
// it to match the directory — a reflex hyphen/underscore edit here silently
// resets every stored mode for every user of this module.
export const MODE_STORAGE_KEY = "defect_reporting.viewMode";

/**
 * Is this a real mode segment? Uses hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" / "toString" / "valueOf" / "__proto__" all
 * test true against an object literal and would pass as valid modes. This
 * module shipped exactly that bug until 2deb1dee; keep the guard.
 */
const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(breadcrumbMenu, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    // Safari private mode / quota-blocked storage throws on access. A remembered
    // view is not worth an unhandled exception on module entry.
    return DEFAULT_MODE_SEGMENT;
  }
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

interface DefectLayoutProps {
  /** Module title from the nav (e.g. "Defect Reporting"); roots the breadcrumb. */
  title?: string;
}

const DefectLayout: React.FC<DefectLayoutProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "defectBreadcrumbMenu",
  });

  // Split the live pathname so the trailing mode segment can be told apart from
  // the (nav-driven) module base. On "/defect/list" that gives basePath
  // "/defect" and currentMode "list".
  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const currentMode = onMode ? lastSegment : DEFAULT_MODE_SEGMENT;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the mode
  // links "//list", which a browser resolves as a PROTOCOL-RELATIVE URL (host
  // "list") and navigates off-site. App.tsx always mounts this module under at
  // least one segment, so that path is unreachable in the app — but it IS
  // reachable in tests (testHelpers pins the URL to "/").
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  const basePath = baseSegments.length ? "/" + baseSegments.join("/") : "";

  useEffect(() => {
    if (!onMode) return;
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, lastSegment);
    } catch {
      // Storage unavailable — the route still shows the right view.
    }
  }, [onMode, lastSegment]);

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>
            {title || "Defect Reporting"} /
          </LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button
              endIcon={<BreadcrumbChevronIcon />}
              sx={breadcrumbCurrentTriggerSx}
            >
              <Typography
                variant="h2"
                component="span"
                sx={breadcrumbCurrentLabelSx}
              >
                {breadcrumbMenu[currentMode]}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(breadcrumbMenu).map(([segment, label]) => (
                <BreadcrumbMenuLinkItem
                  key={segment}
                  to={`${basePath}/${segment}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, segment)}
                  selected={currentMode === segment}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      {/* DefectGrid (list and kanban modes both) renders the shared toolbar's
          create FAB, and its listing now lives one segment below the module
          base ("list"/"kanban"), so the FAB's bare relative "create" would
          resolve to <base>/list/create — a route that does not exist; `create`
          is a SIBLING of the mode routes (index.tsx). Declaring the base here,
          once, covers every mode under this layout, including the two report
          modes if they ever grow a grid of their own. */}
      <ListingRouteBaseProvider value={{ base: basePath }}>
        <Outlet />
      </ListingRouteBaseProvider>
    </Box>
  );
};

export default DefectLayout;
